import { map } from "lodash";
import { Avatar, Box, Flex, StyledLink, Badge } from "orcs-design-system";
import PropTypes from "prop-types";
import React from "react";
import styled from "styled-components";
import { OptionValuePropType } from "src/custom-prop-types/search";

import HeaderMetaItem from "src/commonComponents/ProfileHeader/components/HeaderMetaItem";
import {
  FilledSeatAvatar,
  VacantSeatAvatar,
  VacantSeatIcon,
} from "src/components/VacantSeatAvatar";
import * as searchUtil from "src/util/search";
import { getGroupBadgeVariant } from "src/util/group";
import { getName } from "src/util/person";
import {
  useAllocationConfig,
  useGroupTypes,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { PositionTypeBadge } from "src/components/PositionBadgeLink";
import {
  formatPositionBadgeLabel,
  getPositionId,
  getPositionPerson,
} from "src/util/position";
import ActionButton from "./sub-components/ActionButton";
import Layout from "./sub-components/Layout";
import Placeholder from "./sub-components/Placeholder";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import { trackExploreJourneyEvent } from "src/services/exploreOrganisationAnalytics";

const OCCUPIES_ICON = ["far", "link"];
const POSITION_OCCUPANT_ICON = ["far", "user"];
const POSITION_DATE_ICON = ["far", "calendar-day"];
const POSITION_ID_ICON = ["far", "id-badge"];

const CustomStyledLink = styled(StyledLink)`
  &:hover {
    text-decoration: none;
  }
`;

const OptionBadgeWithActions = ({
  data,
  isLoading,
  theme,
  showPopover,
  selectedString,
  preventNavigation = false,
}) => {
  const groupTypes = useGroupTypes();
  const { enablePositionBackedVacancies } = useWorkspaceFeatureFlags();
  const { vacantRoleLabel } = useAllocationConfig();

  if (isLoading) {
    return <Placeholder theme={theme} />;
  }

  const { actions, badge, link, person, position, CustomActionsComponent } =
    data;

  const isPositionResult = Boolean(position);
  const positionId = isPositionResult ? getPositionId(position) : null;
  const metaSubtitle = badge?.subtitle
    ? searchUtil.highlightSelectedString(badge.subtitle, selectedString)
    : null;
  const occupancyLabel = badge?.occupancyLabel
    ? searchUtil.highlightSelectedString(badge.occupancyLabel, selectedString)
    : null;

  const filledPositions =
    enablePositionBackedVacancies && !person?.isVacancy
      ? person?.filledPositions || []
      : [];

  const supplyTeam = position?.supplyTeam;

  const subTitle = (
    <Flex flexWrap="wrap" alignItems="center" gap="s" mt="xs">
      {isPositionResult ? (
        <>
          {positionId && (
            <HeaderMetaItem
              icon={POSITION_ID_ICON}
              label={searchUtil.highlightSelectedString(
                positionId,
                selectedString
              )}
              popoverContent="Position ID"
              data-testid="search-position-id-meta"
            />
          )}
          {metaSubtitle && (
            <HeaderMetaItem
              icon={POSITION_DATE_ICON}
              label={metaSubtitle}
              title={badge.subtitle}
              data-testid="search-position-dates-meta"
            />
          )}
          {occupancyLabel && (
            <HeaderMetaItem
              icon={POSITION_OCCUPANT_ICON}
              label={occupancyLabel}
              title={badge.occupancyLabel}
              popoverContent={
                badge.occupancyLabel === "Vacant"
                  ? "No one currently fills this seat"
                  : "Filled by"
              }
              data-testid="search-position-occupant-meta"
            />
          )}
        </>
      ) : (
        <>
          <Badge style={{ "text-decoration": "none" }}>{metaSubtitle}</Badge>
          {person?.isVacancy && <PositionTypeBadge />}
        </>
      )}
      {map(filledPositions, (filledPosition) => {
        const positionPerson = getPositionPerson(filledPosition);
        if (!positionPerson?.aggregateId) {
          return null;
        }
        const badgeLabel = formatPositionBadgeLabel(
          vacantRoleLabel,
          getName(positionPerson),
          getPositionId(filledPosition)
        );
        return (
          <HeaderMetaItem
            key={positionPerson.aggregateId}
            icon={OCCUPIES_ICON}
            label={searchUtil.highlightSelectedString(
              badgeLabel,
              selectedString
            )}
            title={badgeLabel}
            popoverContent={badgeLabel}
            data-testid="search-person-position-meta"
          />
        );
      })}
      {supplyTeam?.id && (
        <Badge
          key={supplyTeam.id}
          variant={getGroupBadgeVariant(supplyTeam, groupTypes)}
          data-testid="search-position-supply-team"
        >
          {searchUtil.highlightSelectedString(supplyTeam.name, selectedString)}
        </Badge>
      )}
      {map(searchUtil.teamsToRender(person, groupTypes), (item) => {
        return (
          <Badge key={item.id} variant={getGroupBadgeVariant(item, groupTypes)}>
            {searchUtil.highlightSelectedString(item.name, selectedString)}
          </Badge>
        );
      })}
    </Flex>
  );

  const isOpenSeat = isPositionResult && badge?.isOpenSeat;
  const SeatAvatar = isOpenSeat ? VacantSeatAvatar : FilledSeatAvatar;

  const avatarContent = isPositionResult ? (
    <SeatAvatar
      sizing="small"
      title={searchUtil.highlightSelectedString(badge?.title, selectedString)}
      subtitle={subTitle}
      initials={isOpenSeat ? <VacantSeatIcon /> : badge?.initials}
      image={isOpenSeat ? undefined : badge?.image}
    />
  ) : (
    <Avatar
      sizing="small"
      title={searchUtil.highlightSelectedString(badge?.title, selectedString)}
      subtitle={subTitle}
      initials={badge?.initials}
      image={badge?.image}
    />
  );

  return (
    <Layout theme={theme} hideDivider={data.customStyles}>
      {preventNavigation ? (
        <Box
          display="block"
          width="100%"
          data-testid="cp-option-link"
          data-person-name={badge?.title}
          aria-hidden="true"
          tabIndex="-1"
        >
          {avatarContent}
        </Box>
      ) : (
        <CustomStyledLink
          className="person-link"
          aria-hidden="true"
          tabIndex="-1"
          data-testid="cp-option-link"
          to={link}
          display="block"
          width="100%"
          data-linkto={link}
          data-person-name={badge?.title}
          onClick={() => {
            trackExploreJourneyEvent(EVENT_TRACKING.SEARCH_RESULT_COMPLETED, {
              lifecycle_stage: "completed",
              entry_point: "global_search",
              operation_intent: "search",
              result_type: data?.position ? "position" : "person",
            });
          }}
        >
          {avatarContent}
        </CustomStyledLink>
      )}
      <Flex alignItems="center" gap="xs">
        {CustomActionsComponent ? (
          <CustomActionsComponent />
        ) : (
          map(actions, (action, index) => (
            <ActionButton
              key={`action-btn-${index}`}
              callback={action.callback}
              isDisabled={action.isDisabled}
              icon={action.icon}
              iconSize={action.iconSize}
              text={action.text}
              width={action.width}
              showPopover={showPopover}
            />
          ))
        )}
      </Flex>
    </Layout>
  );
};

OptionBadgeWithActions.propTypes = {
  data: OptionValuePropType,
  isLoading: PropTypes.bool,
  theme: PropTypes.string,
  showPopover: PropTypes.bool,
  selectedString: PropTypes.string,
  preventNavigation: PropTypes.bool,
};

export default OptionBadgeWithActions;
